import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import fr from './locales/fr.json';
import pt from './locales/pt.json';

/**
 * i18n (A14). English is the source + fallback, so any not-yet-translated string
 * still renders (in English) rather than breaking a screen. Language is
 * persisted per device and switched live (no reload) via `setLanguage`.
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'pt', label: 'Português' },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

const STORAGE_KEY = 'snxt_lang';

export function getStoredLanguage(): LanguageCode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && SUPPORTED_LANGUAGES.some((l) => l.code === v)) return v as LanguageCode;
  } catch {
    /* no storage */
  }
  return 'en';
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fr: { translation: fr },
    pt: { translation: pt },
  },
  lng: getStoredLanguage(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.language;
}

/** Switch language live + persist + reflect on <html lang>. */
export function setLanguage(code: LanguageCode): void {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* ignore */
  }
  void i18n.changeLanguage(code);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = code;
  }
}

export default i18n;
